import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import BugZeroSidebar from '../components/bz/BugZeroSidebar';
import BugZeroTopBar from '../components/bz/BugZeroTopBar';
import { contextFromPath } from '../components/bz/products';
import { TenantSettingsProvider } from '../context/TenantSettingsContext';
import { VendorsContext } from '../context/VendorsContext';
import { DashboardContext } from '../context/DashboardContext';
import BZAPIService from '../services/BZAPIService';

const platformSectionFromPath = pathname => {
  // Support + Documentation are platform-level but keep their legacy paths.
  if (pathname.startsWith('/support')) return 'support';
  if (pathname.startsWith('/documentation')) return 'documentation';
  if (!pathname.startsWith('/platform/')) return 'home';
  const seg = pathname.split('/')[2]; // /platform/<seg>/...
  return seg || 'home';
};

const initialsFor = email => {
  if (!email) return 'BZ';
  const [local] = email.split('@');
  const parts = local.split(/[._-]/).filter(Boolean);
  return (parts[0]?.[0] || 'B').toUpperCase() + (parts[1]?.[0] || parts[0]?.[1] || 'Z').toUpperCase();
};

const BugZeroLayout = () => {
  const location = useLocation();
  const contextId = contextFromPath(location.pathname);
  const platformSection = platformSectionFromPath(location.pathname);

  // Vendors + Dashboard data — used to populate the sidebar's Vendors group
  // under Prevent and to drive the legacy Dashboard component. The old
  // NavbarVertical did this; we mirror it here.
  const [vendors, setVendors] = useContext(VendorsContext);
  const [dashboard, setDashboard] = useContext(DashboardContext);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const needVendors = !vendors || vendors.length === 0;
        const needDashboard = !dashboard?.services;
        if (needVendors || needDashboard) {
          const [dash, vList] = await Promise.all([
            needDashboard ? BZAPIService.getDashboard() : Promise.resolve(dashboard),
            needVendors ? BZAPIService.getVendors() : Promise.resolve(vendors)
          ]);
          if (cancelled) return;
          if (needDashboard && dash) setDashboard(dash);
          if (needVendors && Array.isArray(vList)) setVendors(vList);
        }
      } catch (e) {
        // Non-fatal: legacy pages may still surface their own load errors.
        // eslint-disable-next-line no-console
        console.warn('Failed to bootstrap shell data', e);
      }
      try {
        const me = await BZAPIService.getMyUser();
        if (!cancelled && me) {
          setCurrentUser({
            name: me.email || 'Signed in',
            initials: initialsFor(me.email),
            tenant: me.tenantId || ''
          });
        }
      } catch (e) {
        // Non-fatal: footer just shows defaults.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sidebarVendors = useMemo(
    () =>
      (vendors || [])
        .filter(v => v?.type === 'vendor' && v?.active)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(v => ({ id: v.id, name: v.name, family: v.vendorFamily })),
    [vendors]
  );

  return (
    <TenantSettingsProvider>
      <div
        className="bz-app"
        style={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          background: 'var(--bg-soft)'
        }}
      >
        <BugZeroSidebar
          contextId={contextId}
          platformSection={platformSection}
          currentUser={currentUser}
          vendors={sidebarVendors}
        />
        <main
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            flex: 1,
            background: 'var(--bg-soft)'
          }}
        >
          <BugZeroTopBar contextId={contextId} platformSection={platformSection} />
          <div className="bz-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <Outlet />
          </div>
        </main>
      </div>
    </TenantSettingsProvider>
  );
};

export default BugZeroLayout;
